import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  PageHeader,
  SelectField,
  Spinner,
  TextField,
  useConfirm,
  useToast,
} from '@cartly/ui';
import { Plus, Trash2, Users } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { api, errorMessage } from '../../api/client';
import { personName } from '../../app/format';
import { useLoader, useSubmit } from '../../app/hooks';
import { useActiveSession, useSession } from '../../app/session';

type Condition = { field: string; op: string; value?: string | number | boolean };
interface Rules {
  match: 'all' | 'any';
  conditions: Condition[];
}
interface SegmentRow {
  id: string;
  name: string;
  description: string | null;
  rules: Rules;
  customers: number;
}
interface Preview {
  count: number;
  sample: {
    id: string;
    firstName: string | null;
    lastName: string | null;
    email: string | null;
    phone: string | null;
  }[];
}

const FIELDS: Record<
  string,
  {
    label: string;
    ops: { value: string; label: string }[];
    input: 'text' | 'number' | 'none' | 'consent' | 'bool';
  }
> = {
  tag: {
    label: 'Tag',
    ops: [
      { value: 'has', label: 'has' },
      { value: 'not_has', label: 'does not have' },
    ],
    input: 'text',
  },
  ordersCount: {
    label: 'Number of orders',
    ops: [
      { value: 'gte', label: 'at least' },
      { value: 'lte', label: 'at most' },
      { value: 'eq', label: 'exactly' },
    ],
    input: 'number',
  },
  totalSpent: {
    label: 'Total spent',
    ops: [
      { value: 'gte', label: 'at least' },
      { value: 'lte', label: 'at most' },
    ],
    input: 'number',
  },
  lastOrder: {
    label: 'Last order',
    ops: [
      { value: 'within_days', label: 'within days' },
      { value: 'older_than_days', label: 'older than days' },
      { value: 'never', label: 'never ordered' },
    ],
    input: 'number',
  },
  hasPhone: { label: 'Has phone number', ops: [{ value: 'is', label: 'is' }], input: 'bool' },
  whatsappConsent: {
    label: 'WhatsApp consent',
    ops: [{ value: 'is', label: 'is' }],
    input: 'consent',
  },
};

const describe = (c: Condition) => {
  const f = FIELDS[c.field];
  const op = f?.ops.find((o) => o.value === c.op)?.label ?? c.op;
  return `${f?.label ?? c.field} ${op}${c.value === undefined ? '' : ` ${String(c.value)}`}`;
};

function Builder({ onSaved }: { onSaved: () => void }) {
  const toast = useToast();
  const submit = useSubmit();
  const [name, setName] = useState('');
  const [match, setMatch] = useState<'all' | 'any'>('all');
  const [conditions, setConditions] = useState<Condition[]>([
    { field: 'tag', op: 'has', value: '' },
  ]);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const rules = (): Rules => ({
    match,
    conditions: conditions.map((c) => {
      const kind = FIELDS[c.field]?.input;
      if (c.op === 'never') return { field: c.field, op: c.op };
      if (kind === 'number') return { ...c, value: Number(c.value) };
      if (kind === 'bool') return { ...c, value: c.value === true || c.value === 'true' };
      return { ...c, value: String(c.value ?? '') };
    }),
  });
  const set = (i: number, patch: Partial<Condition>) =>
    setConditions((cs) => cs.map((c, j) => (j === i ? { ...c, ...patch } : c)));

  const runPreview = async () => {
    setPreviewError(null);
    try {
      setPreview(await api.post<Preview>('/crm/segments/preview', { rules: rules() }));
    } catch (err) {
      setPreview(null);
      setPreviewError(errorMessage(err));
    }
  };
  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const ok = await submit.run(async () => {
      await api.post('/crm/segments', { name: name.trim(), rules: rules() });
      return true;
    });
    if (ok) {
      toast.success('Segment saved');
      setName('');
      setPreview(null);
      onSaved();
    }
  };

  return (
    <form className="stack" onSubmit={(e) => void onSubmit(e)}>
      {submit.error && <Alert tone="critical">{submit.error}</Alert>}
      <TextField
        label="Name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        maxLength={120}
        required
      />
      <SelectField
        label="Customers who match"
        value={match}
        onChange={(e) => setMatch(e.target.value as 'all' | 'any')}
        options={[
          { value: 'all', label: 'all of these conditions' },
          { value: 'any', label: 'any of these conditions' },
        ]}
      />
      {conditions.map((c, i) => {
        const f = FIELDS[c.field]!;
        return (
          <div key={i} className="row">
            <SelectField
              label="Field"
              value={c.field}
              onChange={(e) => {
                const nf = FIELDS[e.target.value]!;
                set(i, {
                  field: e.target.value,
                  op: nf.ops[0]!.value,
                  value: nf.input === 'consent' ? 'opted_in' : nf.input === 'bool' ? 'true' : '',
                });
              }}
              options={Object.entries(FIELDS).map(([value, v]) => ({ value, label: v.label }))}
            />
            <SelectField
              label="Is"
              value={c.op}
              onChange={(e) => set(i, { op: e.target.value })}
              options={f.ops}
            />
            {c.op !== 'never' &&
              (f.input === 'consent' ? (
                <SelectField
                  label="Value"
                  value={String(c.value ?? 'opted_in')}
                  onChange={(e) => set(i, { value: e.target.value })}
                  options={[
                    { value: 'opted_in', label: 'opted in' },
                    { value: 'opted_out', label: 'opted out' },
                    { value: 'unknown', label: 'no record' },
                  ]}
                />
              ) : f.input === 'bool' ? (
                <SelectField
                  label="Value"
                  value={String(c.value ?? 'true')}
                  onChange={(e) => set(i, { value: e.target.value })}
                  options={[
                    { value: 'true', label: 'yes' },
                    { value: 'false', label: 'no' },
                  ]}
                />
              ) : (
                <TextField
                  label="Value"
                  type={f.input === 'number' ? 'number' : 'text'}
                  value={String(c.value ?? '')}
                  onChange={(e) => set(i, { value: e.target.value })}
                  required
                />
              ))}
            {conditions.length > 1 && (
              <Button
                type="button"
                size="sm"
                variant="secondary"
                onClick={() => setConditions((cs) => cs.filter((_, j) => j !== i))}
              >
                Remove
              </Button>
            )}
          </div>
        );
      })}
      <div className="row">
        <Button
          type="button"
          variant="secondary"
          icon={<Plus size={14} />}
          disabled={conditions.length >= 20}
          onClick={() => setConditions((cs) => [...cs, { field: 'tag', op: 'has', value: '' }])}
        >
          Add condition
        </Button>
        <Button type="button" variant="secondary" onClick={() => void runPreview()}>
          Preview
        </Button>
        <Button type="submit" variant="primary" loading={submit.pending} disabled={!name.trim()}>
          Save segment
        </Button>
      </div>
      {previewError && <Alert tone="critical">{previewError}</Alert>}
      {preview && (
        <Alert
          tone="info"
          title={`${preview.count} matching customer${preview.count === 1 ? '' : 's'}`}
        >
          {preview.sample
            .slice(0, 10)
            .map((c) => personName(c))
            .join(', ') || 'No one matches yet.'}
        </Alert>
      )}
    </form>
  );
}

export function SegmentsPage() {
  const session = useActiveSession();
  const { can } = useSession();
  const toast = useToast();
  const confirm = useConfirm();
  const segments = useLoader(
    () => api.get<{ segments: SegmentRow[] }>('/crm/segments').then((r) => r.segments),
    session.activeTenant?.id,
  );
  const manage = can('customers:manage');

  const remove = async (s: SegmentRow) => {
    const ok = await confirm({
      title: 'Delete segment',
      message: `Delete “${s.name}”? Customers are not affected.`,
      confirmLabel: 'Delete',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await api.delete(`/crm/segments/${s.id}`);
      void segments.reload();
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  return (
    <div className="stack-lg">
      <PageHeader
        title="Segments"
        description="Saved audiences, evaluated live from tags, orders, spend and WhatsApp consent."
        actions={<Link to="/customers">Back to customers</Link>}
      />
      <Card title="Your segments" flush>
        {segments.loading && !segments.data ? (
          <Spinner />
        ) : segments.error ? (
          <Alert tone="critical">{segments.error}</Alert>
        ) : (segments.data ?? []).length === 0 ? (
          <EmptyState icon={<Users size={22} />} title="No segments yet">
            Create one below to group customers for campaigns.
          </EmptyState>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Segment</th>
                  <th>Rules</th>
                  <th>Customers</th>
                  <th className="table__actions">
                    <span className="visually-hidden">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {segments.data!.map((s) => (
                  <tr key={s.id}>
                    <td className="table__primary">{s.name}</td>
                    <td>
                      <div className="text-sm">
                        {s.rules.match === 'all' ? 'All of' : 'Any of'}:
                      </div>
                      {s.rules.conditions.map((c, i) => (
                        <div key={i} className="table__secondary">
                          {describe(c)}
                        </div>
                      ))}
                    </td>
                    <td>
                      <Badge>{s.customers}</Badge>
                    </td>
                    <td className="table__actions">
                      {manage && (
                        <Button
                          size="sm"
                          variant="secondary"
                          icon={<Trash2 size={14} />}
                          aria-label={`Delete ${s.name}`}
                          onClick={() => void remove(s)}
                        >
                          Delete
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      {manage && (
        <Card title="New segment">
          <Builder onSaved={() => void segments.reload()} />
        </Card>
      )}
    </div>
  );
}
